import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2 } from 'lucide-react';
import DataTable, { ColumnDef } from '../../../components/DataTable';
import DataToolbar, { ToolbarAction } from '../../../components/DataToolbar/DataToolbar';
import Pagination from '../../../components/Pagination/Pagination';
import { listarAtivos, listarFundosDoAtivo, AtivoListItem, FundoDoAtivo } from './ativosService';
import '../fundos_home/style.css';

const statusClass = (s: string): string => ((s || '').toLowerCase().includes('inativo') ? 'inativo' : 'ativo');

const dataBr = (iso?: string): string => {
  const v = (iso || '').trim();
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (v || '—');
};

/** Modal read-only: lista os fundos em que um ativo está vinculado. */
const FundosDoAtivoModal: React.FC<{ ativo: AtivoListItem; onClose: () => void }> = ({ ativo, onClose }) => {
  const [fundos, setFundos] = useState<FundoDoAtivo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const r = await listarFundosDoAtivo(ativo.id);
        if (vivo) setFundos(r);
      } catch (e: any) {
        if (vivo) { setErro(e?.message ?? 'Falha ao carregar os fundos.'); setFundos([]); }
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [ativo.id]);

  return ReactDOM.createPortal(
    <div
      onMouseDown={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 10, width: 'min(720px, 92vw)', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,.25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>Fundos do ativo</h3>
            <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
              <strong>{ativo.codigo_interno}</strong> · {ativo.nome_curto || '—'}
            </div>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>

        <div style={{ overflow: 'auto' }}>
          {carregando ? (
            <div style={{ color: '#6b7280', padding: 20 }}>Carregando fundos…</div>
          ) : erro ? (
            <div style={{ color: '#b91c1c', padding: 20 }}>{erro}</div>
          ) : fundos.length === 0 ? (
            <div style={{ color: '#6b7280', padding: 20 }}>Este ativo não está vinculado a nenhum fundo.</div>
          ) : (
            <table className="fiori-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Fundo</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Observação</th>
                </tr>
              </thead>
              <tbody>
                {fundos.map((f) => (
                  <tr key={f.id}>
                    <td>{f.fundo_nome}</td>
                    <td>{dataBr(f.data_inicio)}</td>
                    <td>{f.data_fim ? dataBr(f.data_fim) : '—'}</td>
                    <td>{f.observacao || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer' }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const Portfolio: React.FC = () => {
  const navigate = useNavigate();
  const [ativos, setAtivos] = useState<AtivoListItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(10);
  const [ativoModal, setAtivoModal] = useState<AtivoListItem | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setAtivos(await listarAtivos());
    } catch (e: any) {
      setErro(e?.message ?? 'Falha ao carregar os ativos.');
      setAtivos([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return ativos;
    return ativos.filter((a) =>
      a.codigo_interno.toLowerCase().includes(q) ||
      a.nome_curto.toLowerCase().includes(q) ||
      a.grupo_label.toLowerCase().includes(q) ||
      a.tipo_label.toLowerCase().includes(q) ||
      a.emissor_nome.toLowerCase().includes(q),
    );
  }, [busca, ativos]);

  const inicio = (pagina - 1) * tamanhoPagina;
  const paginaAtual = filtrados.slice(inicio, inicio + tamanhoPagina);

  const colunas = useMemo((): ColumnDef<AtivoListItem>[] => [
    { key: 'codigo_interno', label: 'Código ativo', sortable: true },
    { key: 'grupo_label', label: 'Grupo', sortable: true },
    { key: 'tipo_label', label: 'Subgrupo', sortable: true },
    { key: 'nome_curto', label: 'Nome curto / Phibra', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (a) => <span className={`fh-status fh-status--${statusClass(a.status)}`}>{a.status}</span>,
    },
  ], []);

  const acoes: ToolbarAction[] = [];

  return (
    <div className="fh-home pf-portfolio">
      <DataToolbar
        title="Portfolio de ativos"
        searchValue={busca}
        searchPlaceholder="Buscar por código, nome, grupo, subgrupo ou emissor..."
        onSearchChange={(v) => { setBusca(v); setPagina(1); }}
        actions={acoes}
        primaryLabel="Novo ativo"
        primaryIcon={<Plus size={16} />}
        onPrimary={() => navigate('/cadastro/ativo')}
      />

      {carregando ? (
        <div className="table-container" aria-busy="true" style={{ padding: 24 }}>Carregando ativos...</div>
      ) : (
        <DataTable<AtivoListItem>
          data={paginaAtual}
          columns={colunas}
          rowKey={(a, i) => `${a.id}-${i}`}
          actionsLabel="Fundos"
          renderActions={(a) => (
            <button
              type="button"
              onClick={() => setAtivoModal(a)}
              title="Ver em quais fundos este ativo está"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer',
                fontSize: 13, whiteSpace: 'nowrap',
              }}
            >
              <Building2 size={14} /> Ver fundos
            </button>
          )}
          emptyTitle={erro ? 'Erro ao carregar' : 'Nenhum ativo cadastrado'}
          emptyMessage={erro ?? 'Não há ativos com os filtros aplicados.'}
          emptyHint={erro ? 'Tente novamente em instantes.' : 'Cadastre um ativo em Ativos › Cadastros.'}
        />
      )}

      {!carregando && filtrados.length > 0 && (
        <Pagination
          currentPage={pagina - 1}
          totalPages={Math.ceil(filtrados.length / tamanhoPagina)}
          pageSize={tamanhoPagina}
          totalElements={filtrados.length}
          onPageChange={(p) => setPagina(p + 1)}
          onPageSizeChange={(s) => { setTamanhoPagina(s); setPagina(1); }}
        />
      )}

      {ativoModal && (
        <FundosDoAtivoModal ativo={ativoModal} onClose={() => setAtivoModal(null)} />
      )}
    </div>
  );
};

export default Portfolio;
